import { Injectable } from '@angular/core';
import { SPECTRUM_BASICS_MANUAL } from './basics.manual';
import { HelpManual, HelpTopic, HelpWorkflow } from './guide.model';

export interface HelpTopicRef {
    topic: HelpTopic;
    manual: HelpManual;
}

export interface HelpSearchHit {
    ref: HelpTopicRef;
    score: number;
    /** What matched: a task title, a question, a message, or the topic itself. */
    label: string;
}

/**
 * The manuals this app's Guide knows: its own module's (once loaded) and the
 * short "Getting around Spectrum" manual every app carries. Each Spectrum
 * module is its own app, served under its own path, so only this app's
 * routes are matched against the URL — another module's `/dashboard` is not
 * this one's.
 */
@Injectable({ providedIn: 'root' })
export class GuideRegistry {
    readonly basics: HelpManual = SPECTRUM_BASICS_MANUAL;
    /** The app's own manual, once it has arrived. */
    home: HelpManual | null = null;

    private topics = new Map<string, HelpTopicRef>();
    private textCache = new Map<string, string>();

    constructor() {
        this.index(this.basics);
    }

    /** Help centre order: the module first, then getting around. */
    get manuals(): HelpManual[] {
        return this.home ? [this.home, this.basics] : [this.basics];
    }

    setHome(manual: HelpManual): void {
        this.home = manual;
        this.index(manual);
    }

    private index(manual: HelpManual): void {
        manual.topics.forEach(topic => this.topics.set(topic.id, { topic, manual }));
        this.textCache.clear();
    }

    topicById(id: string | null | undefined): HelpTopicRef | null {
        return (id && this.topics.get(id)) || null;
    }

    manualById(module: string | null | undefined): HelpManual | null {
        return this.manuals.find(manual => manual.module === module) || null;
    }

    /**
     * The topic whose route best fits a URL. `:param` segments match any one
     * segment, and the pattern with the most literal segments wins, so
     * `/students/new` beats `/students/:id`.
     */
    topicForUrl(url: string): HelpTopicRef | null {
        const path = segments(url.split('?')[0].split('#')[0]);
        let best: HelpTopicRef | null = null;
        let bestScore = -1;
        this.topics.forEach(ref => {
            [ref.topic.route, ...(ref.topic.aliases || [])].forEach(pattern => {
                const score = matchScore(segments(pattern || ''), path);
                if (score > bestScore) {
                    best = ref;
                    bestScore = score;
                }
            });
        });
        return best;
    }

    /** The walkthroughs that pass through a topic, with the step it is. */
    workflowsFor(topicId: string): { workflow: HelpWorkflow; step: number }[] {
        const found: { workflow: HelpWorkflow; step: number }[] = [];
        this.manuals.forEach(manual => manual.workflows.forEach(workflow => {
            const step = workflow.steps.findIndex(s => s.topic === topicId);
            if (step >= 0) {
                found.push({ workflow, step });
            }
        }));
        return found;
    }

    workflowById(id: string | null | undefined): HelpWorkflow | null {
        for (const manual of this.manuals) {
            const found = manual.workflows.find(workflow => workflow.id === id);
            if (found) {
                return found;
            }
        }
        return null;
    }

    /**
     * Word search over every topic. Title and keyword hits outrank body text,
     * and a hit on a task, question or message is reported by its own words,
     * since that is usually what somebody searching is looking for.
     */
    search(query: string, limit = 12): HelpSearchHit[] {
        const words = tokens(query);
        if (!words.length) {
            return [];
        }
        const hits: HelpSearchHit[] = [];
        this.topics.forEach(ref => {
            const t = ref.topic;
            let score = 0;
            let label = t.title;
            let labelScore = 0;
            const weigh = (text: string | undefined, weight: number, asLabel = false) => {
                if (!text) {
                    return;
                }
                const lower = text.toLowerCase();
                const found = words.filter(word => lower.includes(word)).length;
                score += found * weight;
                if (asLabel && found > labelScore) {
                    label = text;
                    labelScore = found;
                }
            };
            weigh(t.title, 12);
            weigh((t.keywords || []).join(' '), 8);
            weigh(t.summary, 4);
            t.tasks.forEach(task => { weigh(task.title, 6, true); weigh(task.steps.join(' '), 1); });
            (t.faqs || []).forEach(faq => { weigh(faq.q, 5, true); weigh(faq.a, 1); });
            (t.errors || []).forEach(error => { weigh(`${error.code || ''} ${error.message}`, 6, true); weigh(error.meaning, 1); });
            (t.fields || []).forEach(field => weigh(`${field.term} ${field.meaning}`, 1));
            (t.cautions || []).forEach(caution => weigh(caution, 1));
            const all = words.length > 1 && words.every(word => this.topicText(t).includes(word));
            if (score > 0) {
                hits.push({ ref, score: score + (all ? 10 : 0), label });
            }
        });
        return hits.sort((a, b) => b.score - a.score).slice(0, limit);
    }

    private topicText(t: HelpTopic): string {
        let text = this.textCache.get(t.id);
        if (!text) {
            text = topicAsText(t).toLowerCase() + ' ' + (t.keywords || []).join(' ').toLowerCase();
            this.textCache.set(t.id, text);
        }
        return text;
    }
}

/** A topic as plain text, for search and for anything that wants the whole page at once. */
export function topicAsText(t: HelpTopic): string {
    const lines: string[] = [`${t.title}`, t.summary];
    if (t.audience) lines.push(`Who: ${t.audience}`);
    if (t.access) lines.push(`Access: ${t.access}`);
    if (t.reach) lines.push(`How to get there: ${t.reach}`);
    t.tasks.forEach(task => {
        lines.push(`Task: ${task.title}${task.when ? ` (${task.when})` : ''}`);
        task.steps.forEach((step, i) => lines.push(`  ${i + 1}. ${step}`));
        if (task.result) lines.push(`  Result: ${task.result}`);
    });
    (t.fields || []).forEach(f => lines.push(`${f.term}: ${f.meaning}`));
    (t.cautions || []).forEach(c => lines.push(`Caution: ${c}`));
    (t.tips || []).forEach(c => lines.push(`Tip: ${c}`));
    (t.errors || []).forEach(e => lines.push(`Message ${e.code || ''} "${e.message}": ${e.meaning} What to do: ${e.fix}`));
    (t.faqs || []).forEach(f => lines.push(`Q: ${f.q}\nA: ${f.a}`));
    return lines.join('\n');
}

const STOP = new Set(['the', 'a', 'an', 'to', 'of', 'and', 'or', 'in', 'on', 'for', 'is', 'it', 'i', 'how', 'do', 'what', 'why',
    'can', 'my', 'me', 'this', 'that', 'with', 'be', 'does', 'where', 'when', 'not']);

function tokens(query: string): string[] {
    return (query || '').toLowerCase().split(/[^a-z0-9₹-]+/).filter(word => word.length > 1 && !STOP.has(word));
}

function segments(path: string): string[] {
    return path.split('/').filter(Boolean);
}

function matchScore(pattern: string[], path: string[]): number {
    if (!pattern.length || pattern.length > path.length) {
        return -1;
    }
    let literal = 0;
    for (let i = 0; i < pattern.length; i++) {
        if (pattern[i].startsWith(':')) {
            continue;
        }
        if (pattern[i] !== path[i]) {
            return -1;
        }
        literal++;
    }
    // An exact-length match beats a pattern that only covers the URL's start.
    return (pattern.length === path.length ? 1000 : 0) + literal * 10 + pattern.length;
}
